import { PATH } from "../models"
import { Answer, Question } from "../types"
import { Customer } from "types/entity"
import { SurveyStore, UploadDocStore, VitalsStore } from "types/store"
import { stripUnconfirmedPrefill } from "./prefillProvenance"

export const ANAMNESIS_QUESTION_IDS: string[] = [
  PATH.anamnesis_questions,
  PATH.illness,
  PATH.illness_details,
  PATH.allergy,
  PATH.allergy_details,
  PATH.operation,
  PATH.drug,
  PATH.pregnant,
  PATH.dermato_anamnesis_questions,
  PATH.dermato_history,
  PATH.dermato_family_cancer,
  PATH.dermato_recent_travel,
  PATH.dermato_zone_evolution,
]

export interface SurveySnapshot {
  questions: Question[]
  path: string[]
  answers: Answer[]
  status: SurveyStore["status"]
  currentId?: string
  customerId?: number | string
  savedAt?: number
  // prefill keys already confirmed, edited or answered (never values)
  prefillConsumed?: string[]
}

// Health data must not sit on the kiosk indefinitely: a snapshot older than
// this (or without a savedAt) is treated as absent and cleared at hydration
export const SNAPSHOT_TTL_MS = 60 * 60 * 1000

const isExpired = (savedAt?: number): boolean =>
  !savedAt || Date.now() - savedAt > SNAPSHOT_TTL_MS

// The API customer payload carries no `id`: identify the patient through the
// fields it does expose
export const customerIdentity = (
  customer?: Partial<Customer> & { guid?: string },
): number | string | undefined =>
  customer ? customer.guid ?? customer.id ?? customer.phone : undefined

const DB_NAME = "kiosk_survey"
const DB_VERSION = 2
const STORE_NAME = "survey_state"
const LEGACY_STORE_NAME = "anamnesis_answers"
// The patient answer changed format (-1 + a patient:relative answer before,
// the selected patient id directly now): each format keeps its own key so
// neither build can misread the other's snapshot, including after a rollback.
const SNAPSHOT_KEY = "snapshot_v2"
const LEGACY_SNAPSHOT_KEY = "snapshot"
const VITALS_SNAPSHOT_KEY = "vitals"
const UPLOAD_SNAPSHOT_KEY = "upload"

const destroyDb = (): Promise<void> =>
  new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(DB_NAME)
    request.onsuccess = () => resolve()
    request.onerror = () => resolve()
    request.onblocked = () => resolve()
  })

const openDb = (allowRecovery = true): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("IndexedDB is not available"))
      return
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (db.objectStoreNames.contains(LEGACY_STORE_NAME)) {
        db.deleteObjectStore(LEGACY_STORE_NAME)
      }
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" })
      }
    }
    request.onsuccess = () => {
      const db = request.result
      if (db.objectStoreNames.contains(STORE_NAME)) {
        resolve(db)
        return
      }
      // The database exists at the current version without the expected
      // store: it is only a cache, recreate it from scratch once
      db.close()
      if (!allowRecovery) {
        reject(new Error(`Object store ${STORE_NAME} is missing`))
        return
      }
      destroyDb().then(() => openDb(false).then(resolve, reject))
    }
    request.onerror = () => {
      // A database left at a higher version (e.g. by another build) cannot
      // be opened: recreate it from scratch once
      if (allowRecovery && request.error?.name === "VersionError") {
        destroyDb().then(() => openDb(false).then(resolve, reject))
        return
      }
      reject(request.error)
    }
  })

const withStore = <T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> =>
  openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode)
        const request = run(transaction.objectStore(STORE_NAME))
        transaction.oncomplete = () => {
          db.close()
          resolve(request ? request.result : undefined)
        }
        transaction.onerror = () => {
          db.close()
          reject(transaction.error)
        }
      }),
  )

// Functions and React elements (dynamic reply icons) cannot be persisted;
// they are regenerated by the sagas after hydration.
const toSerializable = <T>(value: T): T =>
  JSON.parse(
    JSON.stringify(value, (_key, val) => {
      if (typeof val === "function") return undefined
      if (val && typeof val === "object" && val.$$typeof) return undefined
      return val
    }),
  )

export const persistSurveySnapshot = async (
  survey: Partial<SurveyStore>,
  customerId?: number | string,
): Promise<void> => {
  try {
    // Prefilled values the patient has not validated yet never reach the disk
    const persistable = stripUnconfirmedPrefill(
      {
        questions: (survey.questions as Question[]) || [],
        path: survey.path || [],
        answers: survey.answers || [],
      },
      survey.prefill,
    )
    const snapshot: SurveySnapshot & { id: string } = toSerializable({
      id: SNAPSHOT_KEY,
      ...persistable,
      status: survey.status || "on_going",
      currentId: survey.current?.id,
      customerId,
      savedAt: Date.now(),
    })
    await withStore("readwrite", (store) => {
      store.put(snapshot)
    })
  } catch (error) {
    console.error(error, { route: "survey.snapshot.persist" })
  }
}

export const loadPersistedSurveySnapshot = async (): Promise<
  SurveySnapshot | undefined
> => {
  try {
    const legacy = await withStore<SurveySnapshot>("readonly", (store) =>
      store.get(LEGACY_SNAPSHOT_KEY),
    )
    if (legacy) {
      // Old-format consultation in progress: drop it entirely (survey,
      // vitals and upload describe the same consultation)
      await withStore("readwrite", (store) => store.clear())
      return undefined
    }
    const snapshot = await withStore<SurveySnapshot>("readonly", (store) =>
      store.get(SNAPSHOT_KEY),
    )
    if (!snapshot || isExpired(snapshot.savedAt)) return undefined
    return snapshot
  } catch (error) {
    console.error(error, { route: "survey.snapshot.load" })
    return undefined
  }
}

export interface VitalsSnapshot {
  vitals: Partial<VitalsStore>
  customerId?: number | string
}

export const persistVitalsSnapshot = async (
  vitals: Partial<VitalsStore>,
  customerId?: number | string,
): Promise<void> => {
  try {
    const snapshot = toSerializable({
      id: VITALS_SNAPSHOT_KEY,
      vitals: vitals || {},
      customerId,
    })
    await withStore("readwrite", (store) => {
      store.put(snapshot)
    })
  } catch (error) {
    console.error(error, { route: "vitals.snapshot.persist" })
  }
}

export const loadPersistedVitalsSnapshot = async (): Promise<
  VitalsSnapshot | undefined
> => {
  try {
    return await withStore<VitalsSnapshot>("readonly", (store) =>
      store.get(VITALS_SNAPSHOT_KEY),
    )
  } catch (error) {
    console.error(error, { route: "vitals.snapshot.load" })
    return undefined
  }
}

// Captured photos are blob: URLs, which do not survive a reload. The
// underlying Blob is stored instead, and a fresh object URL is created at
// hydration so the recap keeps its blob-URL rendering path (PDF iframes).
interface PersistedPhoto {
  url?: string
  blob?: Blob
}

export interface UploadSnapshot {
  documents: any[]
  capturedDermatoPhotos: string[]
  capturedPhonePhotos: string[]
  phonePhotoKeys: string[]
  processedPhonePhotoDocIds: Array<string | number>
  customerId?: number | string
}

interface PersistedUploadSnapshot {
  id: string
  documents: any[]
  dermatoPhotos: PersistedPhoto[]
  phonePhotos: PersistedPhoto[]
  // Persisted with the photos so a reload does not re-import the same
  // documents (the polling would otherwise see them as unprocessed)
  phonePhotoKeys?: string[]
  processedPhonePhotoDocIds?: Array<string | number>
  customerId?: number | string
}

const toPersistedPhoto = async (url: string): Promise<PersistedPhoto> => {
  if (!url.startsWith("blob:")) return { url }
  const blob = await fetch(url).then((response) => response.blob())
  return { blob }
}

const toDisplayablePhoto = (photo: PersistedPhoto): string | undefined =>
  photo.blob ? URL.createObjectURL(photo.blob) : photo.url

export const persistUploadSnapshot = async (
  upload: Partial<UploadDocStore>,
  customerId?: number | string,
): Promise<void> => {
  try {
    const [dermatoPhotos, phonePhotos] = await Promise.all([
      Promise.all((upload.capturedDermatoPhotos || []).map(toPersistedPhoto)),
      Promise.all((upload.capturedPhonePhotos || []).map(toPersistedPhoto)),
    ])
    const snapshot: PersistedUploadSnapshot = {
      id: UPLOAD_SNAPSHOT_KEY,
      documents: toSerializable(upload.documents || []),
      dermatoPhotos,
      phonePhotos,
      phonePhotoKeys: upload.phonePhotoKeys || [],
      processedPhonePhotoDocIds: upload.processedPhonePhotoDocIds || [],
      customerId,
    }
    await withStore("readwrite", (store) => {
      store.put(snapshot)
    })
  } catch (error) {
    console.error(error, { route: "upload.snapshot.persist" })
  }
}

export const loadPersistedUploadSnapshot = async (): Promise<
  UploadSnapshot | undefined
> => {
  try {
    const snapshot = await withStore<PersistedUploadSnapshot>(
      "readonly",
      (store) => store.get(UPLOAD_SNAPSHOT_KEY),
    )
    if (!snapshot) return undefined
    return {
      documents: snapshot.documents || [],
      capturedDermatoPhotos: (snapshot.dermatoPhotos || [])
        .map(toDisplayablePhoto)
        .filter(Boolean) as string[],
      capturedPhonePhotos: (snapshot.phonePhotos || [])
        .map(toDisplayablePhoto)
        .filter(Boolean) as string[],
      phonePhotoKeys: snapshot.phonePhotoKeys || [],
      processedPhonePhotoDocIds: snapshot.processedPhonePhotoDocIds || [],
      customerId: snapshot.customerId,
    }
  } catch (error) {
    console.error(error, { route: "upload.snapshot.load" })
    return undefined
  }
}

// The survey, vitals and upload snapshots describe the same in-progress
// consultation: they are always cleared together
export const clearPersistedSurveySnapshot = async (): Promise<void> => {
  try {
    await withStore("readwrite", (store) => store.clear())
  } catch (error) {
    console.error(error, { route: "survey.snapshot.clear" })
  }
}
